import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const PricingPage = () => {
  const [activeTab, setActiveTab] = useState('ussd');
  const [country, setCountry] = useState('Kenya');

  const ussdPricing = [
    {
      digits: '1 digit, e.g *123*X#',
      monthlyFee: '15,000',
      costPerSession: 'KES 0.04 per leg',
      setupCost: 'KES 0'
    },
    {
      digits: '2 digits, e.g *123*XX#',
      monthlyFee: '10,000',
      costPerSession: 'KES 0.04 per leg',
      setupCost: 'KES 0'
    },
    {
      digits: '3 digits, e.g *123*XXX#',
      monthlyFee: '5,000',
      costPerSession: 'KES 0.04 per leg',
      setupCost: 'KES 0'
    },
    {
      digits: '4 digits, e.g *123*XXXX#',
      monthlyFee: '2,000',
      costPerSession: 'KES 0.034 per leg',
      setupCost: 'KES 0'
    },
    {
      digits: '5 digits e.g *123*XXXXX#',
      monthlyFee: '200 (Student Package)',
      costPerSession: 'KES 0.05 per leg',
      setupCost: 'KES 0'
    }
  ];

  const emailPricing = [
    {
      tier: '1 - 1000',
      pricePerEmail: '1',
      totalPrice: '1,000'
    },
    {
      tier: '1001 - 10,000',
      pricePerEmail: '0.8',
      totalPrice: '8,000'
    },
    {
      tier: '10,001 - 100,000',
      pricePerEmail: '0.6',
      totalPrice: '60,000'
    },
    {
      tier: '100,001 - 500,000',
      pricePerEmail: '0.5',
      totalPrice: '250,000'
    },
    {
      tier: '500,001 - 1,000,000',
      pricePerEmail: '0.4',
      totalPrice: '400,000'
    },
    {
      tier: '1,000,001+',
      pricePerEmail: '0.3',
      totalPrice: '300,000'
    }
  ];

  const tabs = [
    { id: 'bulksms', name: 'BULK SMS', available: false },
    { id: 'email', name: 'EMAIL SERVICE', available: true },
    { id: 'ussd', name: 'USSD', available: true },
    { id: 'shortcode', name: 'SHORT CODE', available: false },
    { id: 'survey', name: 'SURVEY', available: false }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-400 to-teal-500 text-white py-16 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Pricing</h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            We deliver exceptional services at competitive rates, giving our clients a seamless and budget-friendly experience. Our mission is to make premium solutions accessible to all, maintaining excellence while keeping costs low, so businesses can grow and succeed without breaking the bank.
          </p>
        </div>
      </div>

      {/* Country Selector */}
      <div className="container mx-auto px-4 py-8 flex justify-center">
        <div className="relative">
          <select 
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="bg-blue-900 text-white px-8 py-3 rounded-full appearance-none pr-12 font-medium cursor-pointer hover:bg-blue-800 transition-colors"
          >
            <option>Kenya</option>
            <option>Uganda</option>
            <option>Tanzania</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Service Tabs */}
      <div className="container mx-auto px-4 pb-8">
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <div className="flex border-b">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => tab.available && setActiveTab(tab.id)}
                className={`flex-1 min-w-max px-6 py-4 font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'text-teal-500 border-b-2 border-teal-500 bg-teal-50'
                    : tab.available
                    ? 'text-gray-600 hover:text-teal-500 hover:bg-gray-50'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                disabled={!tab.available}
              >
                {tab.name}
                {!tab.available && (
                  <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Coming Soon</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Tables */}
      <div className="container mx-auto px-4 pb-16">
        {activeTab === 'ussd' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4">
              <h2 className="text-2xl font-bold">Shared USSD Pricing Table</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Digits</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Monthly Fees (KES)<br/><span className="font-normal text-xs">VAT inclusive</span></th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Cost Per Session (Leg)</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Setup Costs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {ussdPricing.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-gray-700">{item.digits}</td>
                      <td className="px-6 py-4 text-gray-900 font-semibold">{item.monthlyFee}</td>
                      <td className="px-6 py-4 text-gray-700">{item.costPerSession}</td>
                      <td className="px-6 py-4 text-gray-700">{item.setupCost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-blue-50 px-6 py-4 border-t">
              <div className="flex items-start space-x-2">
                <Check className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                <p className="text-sm text-gray-700">All prices include VAT. No hidden charges. Choose the package that best fits your business needs.</p>
              </div>
            </div>
            
            {/* Golden USSD Codes Section */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 px-6 py-8 border-t-4 border-amber-400">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg">
                  ✨ Golden USSD Codes ✨
                </div>
              </div>
              <p className="text-center text-gray-700 mb-6 font-medium">
                For a USSD code to be considered a <span className="text-amber-600 font-bold">golden code</span>, it should either:
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
                <div className="bg-white rounded-lg p-4 shadow-md border-2 border-amber-200 hover:border-amber-400 transition-all">
                  <div className="font-semibold text-amber-700 mb-2">1. Same Digits</div>
                  <p className="text-sm text-gray-600 mb-2">e.g. *710*22#, *710*333#, *710*4444#, etc.</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-md border-2 border-amber-200 hover:border-amber-400 transition-all">
                  <div className="font-semibold text-amber-700 mb-2">2. Tens, Hundreds & Thousands</div>
                  <p className="text-sm text-gray-600 mb-2">e.g. *710*20#, *710*300#, *710*4000#, etc.</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-md border-2 border-amber-200 hover:border-amber-400 transition-all">
                  <div className="font-semibold text-amber-700 mb-2">3. Symmetric Numbers</div>
                  <p className="text-sm text-gray-600 mb-2">e.g. *710*323#, *710*4141#, etc. <span className="text-xs italic">(2 digits excluded)</span></p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-md border-2 border-amber-200 hover:border-amber-400 transition-all">
                  <div className="font-semibold text-amber-700 mb-2">4. Repeated Digits</div>
                  <p className="text-sm text-gray-600 mb-2">e.g. *710*422#, *710*224#, *710*4222#, *710*2224#, etc. <span className="text-xs italic">(2 digits excluded)</span></p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-md border-2 border-amber-200 hover:border-amber-400 transition-all">
                  <div className="font-semibold text-amber-700 mb-2">5. Easy To Remember</div>
                  <p className="text-sm text-gray-600 mb-2">e.g. *710*3434#, *710*5656#, *710*2244#, *710*22255#, etc. <span className="text-xs italic">(2 digits excluded)</span></p>
                </div>
              </div>
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 italic">
                  <span className="text-amber-600 font-semibold">Note:</span> 2 digits codes are excluded from Symmetric, Repeated, and Sequential golden code categories.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'email' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-4">
              <h2 className="text-2xl font-bold">Emailpro Pricing</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Tier (Email)</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Price per Email (KES)</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Total Price (KES)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {emailPricing.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-gray-700 font-medium">{item.tier}</td>
                      <td className="px-6 py-4 text-gray-900 font-semibold">{item.pricePerEmail}</td>
                      <td className="px-6 py-4 text-teal-600 font-bold">{item.totalPrice}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-teal-50 px-6 py-4 border-t">
              <div className="flex items-start space-x-2">
                <Check className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                <p className="text-sm text-gray-700">Volume-based pricing ensures you get the best rates as you scale. Perfect for businesses of all sizes.</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bulksms' && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Bulk SMS Pricing</h3>
              <p className="text-gray-600 mb-6">Contact Our Team To Customize your Own Pricing</p>
              <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                Contact Us for Details
              </button>
            </div>
          </div>
        )}

        {activeTab === 'shortcode' && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Short Code Pricing</h3>
              <p className="text-gray-600 mb-6">Pricing details for Short Code services will be available soon. Stay tuned!</p>
              <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                Contact Us for Details
              </button>
            </div>
          </div>
        )}

        {activeTab === 'survey' && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Survey Service Pricing</h3>
              <p className="text-gray-600 mb-6">Pricing details for Survey services will be available soon. Stay tuned!</p>
              <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                Contact Us for Details
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need a Custom Solution?</h2>
          <p className="text-lg mb-6 max-w-2xl mx-auto">
            We understand that every business is unique. Contact us for tailored pricing packages that fit your specific requirements.
          </p>
          <Link to="/contact" className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-block text-center">
  Get in Touch
</Link>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;